/* PushAutoEnablePrompt — beautiful one-time soft pre-prompt that nudges the
   user to enable lock-screen notifications. Mounts globally; only renders on
   first session for an authenticated user whose browser permission is still
   "default" (i.e. never asked). Persists dismissal in localStorage.

   We use a soft pre-prompt because once a user clicks the native browser
   "Block" button, push is dead for that origin until they manually re-enable
   it in browser settings. This pattern (used by NYT, Slack, Notion, etc.)
   roughly triples opt-in rates. */
import { useEffect, useState } from "react";
import { BellRing, X } from "lucide-react";
import { useUser } from "../../context/UserAuthContext";
import {
  pushSupported,
  enablePush,
  getCurrentPushSubscription,
} from "../../lib/pushApi";

const DISMISS_KEY = "lexiq.push.prompt.dismissed";
const SHOW_DELAY_MS = 2200;

export default function PushAutoEnablePrompt() {
  const { user, loading } = useUser();
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loading || !user) return;
    if (!pushSupported()) return;
    // Already decided in the browser? do nothing.
    if (typeof Notification === "undefined") return;
    if (Notification.permission !== "default") return;
    // User explicitly dismissed before? respect that for 30 days.
    try {
      const v = localStorage.getItem(DISMISS_KEY);
      if (v && Date.now() - Number(v) < 30 * 24 * 60 * 60 * 1000) return;
    } catch (_) { /* ignore */ }

    let cancelled = false;
    (async () => {
      try {
        const sub = await getCurrentPushSubscription();
        if (cancelled || sub) return; // already subscribed
        setTimeout(() => { if (!cancelled) setShow(true); }, SHOW_DELAY_MS);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [user, loading]);

  const dismiss = (snooze = true) => {
    if (snooze) {
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) {}
    }
    setShow(false);
  };

  const handleEnable = async () => {
    setBusy(true);
    try {
      await enablePush();
      try { localStorage.removeItem(DISMISS_KEY); } catch (_) {}
      setShow(false);
    } catch {
      // Browser denial or other error — close softly, snooze for a month.
      dismiss(true);
    } finally {
      setBusy(false);
    }
  };

  if (!show) return null;

  return (
    <div
      data-testid="push-auto-prompt"
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-slate-950/40 backdrop-blur-sm animate-[fadeIn_200ms_ease-out]"
      style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)" }}
      onClick={() => dismiss(true)}
    >
      <div
        className="w-full sm:w-[420px] mx-4 bg-white rounded-3xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        dir="rtl"
      >
        <div className="relative px-6 pt-8 pb-5 bg-gradient-to-b from-[#0A192F] to-[#142a4d] text-white text-center">
          <button
            data-testid="push-prompt-close"
            onClick={() => dismiss(true)}
            className="absolute top-3 left-3 p-1.5 rounded-full text-white/60 hover:text-white hover:bg-white/10 transition"
            aria-label="إغلاق"
          >
            <X size={16} />
          </button>
          <div className="mx-auto mb-3 w-14 h-14 rounded-2xl bg-white/10 ring-1 ring-white/15 flex items-center justify-center">
            <BellRing size={26} className="text-amber-300" strokeWidth={1.8} />
          </div>
          <h3 className="text-base font-bold leading-tight">
            ابقَ على اطّلاع بكل ما يخص قضاياك
          </h3>
          <p className="mt-2 text-[12.5px] text-white/75 leading-relaxed px-2">
            فعّل الإشعارات لتصلك تنبيهات الجلسات القادمة، قرارات التمييز الجديدة،
            وأي ردّ على قضاياك — حتى وإن كانت شاشتك مقفولة.
          </p>
        </div>

        <div className="px-6 py-5 flex flex-col gap-2.5">
          <button
            data-testid="push-prompt-enable"
            onClick={handleEnable}
            disabled={busy}
            className="w-full py-2.5 rounded-xl bg-[#0A192F] hover:bg-[#0e2243] disabled:opacity-60 text-white text-sm font-bold transition"
          >
            {busy ? "جاري التفعيل…" : "تفعيل الإشعارات"}
          </button>
          <button
            data-testid="push-prompt-later"
            onClick={() => dismiss(true)}
            className="w-full py-2 text-xs font-medium text-slate-500 hover:text-slate-700 transition"
          >
            ليس الآن
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
      `}</style>
    </div>
  );
}
